/**
 * @fileoverview Doctor Schedule Page
 * @description Weekly calendar view of doctor's schedule with assignment details
 * @author MCHD Room Scheduler Team
 * @created 07-10-2025
 * @complies @ui-ux-excellence.mdc - WCAG 2.1 AA accessibility compliance
 */

import { useState } from 'react';
import { Container, Title, Text, Stack, Alert } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { WeeklyCalendarView } from '../../features/doctor/schedule/WeeklyCalendarView';
import { AssignmentDetailsModal } from '../../features/doctor/schedule/AssignmentDetailsModal';
import type { DoctorAssignment } from '../../types/doctor';

/**
 * Doctor Schedule Page Component
 * @description Interactive weekly calendar showing doctor's assignments
 * 
 * @example
 * Route: /doctor/schedule
 * Access: Doctors only (requireDoctor middleware)
 * 
 * Features:
 * - Weekly calendar view (Sunday-Saturday)
 * - Week navigation (prev/next, go to current week)
 * - Assignment details modal on click
 * - Keyboard navigation support
 * - Status indicators (Today, Tomorrow, Past)
 * - Real-time data updates every 3 minutes
 */
export default function DoctorSchedulePage() {
  // Selected assignment state for modal
  const [selectedAssignment, setSelectedAssignment] = useState<DoctorAssignment | null>(null);

  /**
   * Handle assignment click - open details modal
   */
  const handleAssignmentClick = (assignment: DoctorAssignment) => {
    setSelectedAssignment(assignment);
  };

  /**
   * Handle modal close
   */
  const handleCloseModal = () => {
    setSelectedAssignment(null);
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        {/* Page Header */}
        <div>
          <Title order={1} mb="xs">
            My Schedule
          </Title>
          <Text c="dimmed" size="sm">
            Weekly view of your published assignments
          </Text>
        </div>

        {/* Info Alert */}
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Published Schedules Only"
          color="blue"
          variant="light"
        >
          <Text size="sm">
            Only published assignments are shown. Contact your coordinator if you expect to see additional sessions.
          </Text>
        </Alert>

        {/* Weekly Calendar */}
        <WeeklyCalendarView
          onAssignmentClick={handleAssignmentClick}
          maxHeight={600}
        />

        {/* Assignment Details Modal */}
        <AssignmentDetailsModal
          assignment={selectedAssignment}
          opened={!!selectedAssignment}
          onClose={handleCloseModal}
        />

        {/* Instructions */}
        <Text size="xs" c="dimmed" ta="center">
          Click on any assignment to view detailed information. Use arrow buttons to navigate between weeks.
        </Text>
      </Stack>
    </Container>
  );
}

